import {
  dateFormats,
  formattedDateTime,
} from "@circle-react/helpers/dateTimeHelpers/formattedDateTime";
import type { UpgradePreview } from "@circle-react/types";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";

import { uniqueUpgradeCoupons } from "./helpers/uniqueUpgradeCoupons";
import { UpgradeOrderCouponChip } from "./UpgradeOrderCouponChip";
import { UpgradeOrderRow } from "./UpgradeOrderRow";
import { UpgradeTrialOrderDetails } from "./UpgradeTrialOrderDetails";

const i18nRoot = "paywall_checkout.offer_page.upgrade";
const UPGRADE_LABEL_KEYS = {
  SUBTOTAL: "subtotal",
  COUPON_APPLIED: "coupon_applied",
  PREVIOUS_PLAN_CREDIT: "previous_plan_credit",
  TOTAL_DUE_TODAY: "total_due_today",
  RENEWAL_PRICE: "renewal_price",
  TAX: "tax",
  TAX_INCLUDED: "tax_included",
} as const;

export const UpgradeOrderDetails = ({
  preview,
}: {
  preview: UpgradePreview;
}) => {
  const isInPlaceEstimate =
    preview.execution_lane === "in_place" &&
    preview.trial.disposition === "target";
  const estimateLabel = (key: string) =>
    t([i18nRoot, isInPlaceEstimate ? `estimated_${key}` : key]);
  const tax = preview.today.tax;
  const couponAdjustment = preview.today.coupon_adjustment;
  const credit = preview.today.previous_plan_credit;
  const discounts = preview.coupon.effective_discounts.filter(
    discount =>
      (discount.application_invoice === "today" &&
        (discount.today_amount?.amount ?? 0) > 0) ||
      (discount.application_invoice === "first_paid_renewal" &&
        (discount.first_paid_renewal_amount?.amount ?? 0) > 0),
  );
  const renewalDate = (at: string) =>
    formattedDateTime({
      dateTime: new Date(at),
      format: dateFormats.short_month_and_date_with_year,
    });

  if (
    ["target", "preserved", "carried_forward"].includes(
      preview.trial.disposition,
    ) &&
    preview.today.total.amount === 0 &&
    preview.today.subtotal.amount === 0 &&
    !credit &&
    !couponAdjustment &&
    !tax?.amount.amount
  ) {
    return <UpgradeTrialOrderDetails preview={preview} />;
  }

  return (
    <>
      {isInPlaceEstimate && (
        <Typography
          className="mb-4"
          variant="body-sm"
          color="secondary"
          data-testid="upgrade-estimated-terms-disclaimer"
        >
          {t([i18nRoot, "estimated_amounts_note"])}
        </Typography>
      )}
      <div className="space-y-3" data-testid="upgrade-order-details-rows">
        <UpgradeOrderRow
          label={preview.labels.selected_plan}
          amount={preview.destination.list_amount}
          variant="plan"
        />
        <UpgradeOrderRow
          label={estimateLabel(UPGRADE_LABEL_KEYS.SUBTOTAL)}
          amount={preview.today.subtotal}
        />
        {tax && (
          <UpgradeOrderRow
            label={estimateLabel(
              tax.treatment === "inclusive"
                ? UPGRADE_LABEL_KEYS.TAX_INCLUDED
                : UPGRADE_LABEL_KEYS.TAX,
            )}
            amount={tax.amount}
          />
        )}
        {couponAdjustment && (
          <UpgradeOrderRow
            label={estimateLabel(UPGRADE_LABEL_KEYS.COUPON_APPLIED)}
            amount={couponAdjustment}
            variant="discount"
          />
        )}
        {uniqueUpgradeCoupons(discounts).map(discount => (
          <div key={discount.discount_id} className="py-2">
            <UpgradeOrderCouponChip discount={discount} />
          </div>
        ))}
        {credit && (
          <UpgradeOrderRow
            label={estimateLabel(UPGRADE_LABEL_KEYS.PREVIOUS_PLAN_CREDIT)}
            amount={credit}
            variant="discount"
          />
        )}
      </div>
      <div
        className="border-primary mt-6 space-y-4 border-t pt-6"
        data-testid="upgrade-order-details-totals"
      >
        <UpgradeOrderRow
          label={estimateLabel(UPGRADE_LABEL_KEYS.TOTAL_DUE_TODAY)}
          amount={preview.today.total}
          variant="total"
        />
        <UpgradeOrderRow
          label={estimateLabel(UPGRADE_LABEL_KEYS.RENEWAL_PRICE)}
          amount={preview.first_paid_renewal.amount}
          detail={
            <div className="mt-1 flex flex-col gap-1">
              <Typography variant="body-sm" color="secondary">
                {preview.first_paid_renewal.amount
                  ? t(
                      [
                        i18nRoot,
                        isInPlaceEstimate
                          ? "estimated_plan_renews_on"
                          : "plan_renews_on",
                      ],
                      { date: renewalDate(preview.first_paid_renewal.at) },
                    )
                  : t([i18nRoot, "renewal_amount_unavailable"])}
              </Typography>
            </div>
          }
          variant="plan"
        />
        {preview.later_undiscounted_renewal && (
          <UpgradeOrderRow
            label={preview.later_undiscounted_renewal.label}
            amount={preview.later_undiscounted_renewal.amount}
            detail={t(
              [
                i18nRoot,
                isInPlaceEstimate
                  ? "estimated_plan_renews_on"
                  : "plan_renews_on",
              ],
              {
                date: renewalDate(preview.later_undiscounted_renewal.at),
              },
            )}
          />
        )}
      </div>
    </>
  );
};
